import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import APSLogo from "../../../components/ui/APS Logo.svg";

const POSTS = [
  {
    date: "April 10, 2026",
    tag: "Product",
    title: "How AIPolicyScan detects policy changes before they affect your compliance posture",
    summary:
      "Vendor privacy policies change quietly. We break down the delta-detection engine that alerts your team within hours of a meaningful update.",
  },
  {
    date: "March 28, 2026",
    tag: "Compliance",
    title: "The hidden cost of manual privacy policy reviews",
    summary:
      "Most compliance teams spend 4–6 hours reviewing a single vendor's privacy documentation. Here's what that adds up to across a 200-vendor portfolio.",
  },
  {
    date: "March 14, 2026",
    tag: "Guide",
    title: "What to look for in a vendor's privacy policy: a practical checklist",
    summary:
      "Not all privacy policies are created equal. This guide covers the 12 clauses that matter most for GDPR, CCPA, and enterprise data governance.",
  },
];

const TAG_COLORS = {
  Product: "bg-[#ECE9FF] text-[#5B4FCF]",
  Compliance: "bg-[#E8F7EE] text-[#177B43]",
  Guide: "bg-[#FFF3E0] text-[#C27A00]",
};

const BlogPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav bar */}
      <header className="border-b border-surface-border px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={APSLogo} alt="APS Logo" className="h-11 w-auto object-contain" />
        </Link>
        <Link to="/" className="text-sm text-brand-muted hover:text-primary transition-colors">
          ← Back to home
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="mb-12">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#ECE9FF] mb-5">
            <BookOpen className="w-7 h-7 text-[#5B4FCF]" />
          </div>
          <h1 className="text-4xl font-extrabold text-brand-text mb-3">Blog</h1>
          <p className="text-base text-brand-muted leading-relaxed">
            Insights on privacy compliance, vendor risk, and AI-assisted policy analysis.
          </p>
        </div>

        {/* Posts */}
        <div className="flex flex-col gap-6">
          {POSTS.map((post) => (
            <article
              key={post.title}
              className="bg-[#FAFAFA] rounded-2xl border border-surface-border px-6 py-6 hover:border-[#C4BEFF] hover:shadow-sm transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-3">
                <span
                  className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${
                    TAG_COLORS[post.tag] ?? "bg-surface text-brand-muted"
                  }`}
                >
                  {post.tag}
                </span>
                <span className="text-xs text-brand-light">{post.date}</span>
              </div>
              <h2 className="text-base font-bold text-brand-text mb-2 leading-snug">{post.title}</h2>
              <p className="text-sm text-brand-muted leading-relaxed">{post.summary}</p>
              <p className="mt-4 text-sm font-semibold text-[#5B4FCF] hover:underline">
                Read more →
              </p>
            </article>
          ))}
        </div>

        {/* Coming soon note */}
        <div className="mt-10 text-center">
          <p className="text-sm text-brand-muted">
            More articles coming soon.{" "}
            <a href="mailto:hello@aipolicyscan.ai" className="text-primary hover:underline">
              Subscribe for updates
            </a>
            .
          </p>
        </div>

        {/* Footer note */}
        <div className="mt-12 pt-8 border-t border-surface-border text-center">
          <p className="text-xs text-brand-light">© 2026 AIPolicyScan. All rights reserved.</p>
          <div className="flex justify-center gap-5 mt-3">
            <Link to="/privacy-policy" className="text-xs text-brand-muted hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-xs text-brand-muted hover:text-primary transition-colors">
              Terms
            </Link>
            <Link to="/" className="text-xs text-brand-muted hover:text-primary transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BlogPage;
